import useQueryParams from 'hooks/useQueryParams'
import { useCallback, useMemo } from 'react'
import { replace } from 'connected-react-router'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'

const SEARCH_FILTERS_BOUNDS = 'bounds'
const SEARCH_FILTERS_PRICE_LTE = 'priceLte'
const SEARCH_FILTERS_PRICE_GTE = 'priceGte'
const SEARCH_FILTERS_CATEGORY_CODES = 'categories'
const SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE = 'luxPlusFeatures'

export default function useExperiencesSearchFilters() {
  const appDispatch = useAppDispatch()
  const queryParameters = useQueryParams()
  const isExperiencesLuxPlusEnabled = useAppSelector((state) => getIsEnabledLuxPlusDiscountedExperiences(state))

  const replaceSearchUrl = (newUrlFilters: URLSearchParams) => {
    return appDispatch(replace({
      search: newUrlFilters.toString(),
    }))
  }

  const setSearchFilters = useCallback((newFilters: Experiences.SearchFilters) => {
    const newUrlFilters = convertSearchFiltersToUrl(newFilters, queryParameters)
    replaceSearchUrl(newUrlFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const searchFilters = useMemo<Experiences.SearchFilters>(() => {
    const filters = extractSearchFiltersFromUrl(queryParameters)

    // If LuxPlus is not enabled, remove the LuxPlus category from the filters
    if (!isExperiencesLuxPlusEnabled) {
      filters.categoryCodes = filters.categoryCodes?.filter(code => ![EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID].includes(code))
    }
    return filters
  }, [queryParameters, isExperiencesLuxPlusEnabled])

  const setCategoryCodesSearchFilter = useCallback((categoryCodes: Array<number>) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (categoryCodes?.length) {
      newUrlFilters.set(SEARCH_FILTERS_CATEGORY_CODES, categoryCodes.join(','))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_CATEGORY_CODES)
    }
    replaceSearchUrl(newUrlFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setPriceLteSearchFilter = useCallback((priceLte: number) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (priceLte) {
      newUrlFilters.set(SEARCH_FILTERS_PRICE_LTE, String(priceLte))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_PRICE_LTE)
    }
    replaceSearchUrl(newUrlFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setPriceGteSearchFilter = useCallback((priceGte: number) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (priceGte) {
      newUrlFilters.set(SEARCH_FILTERS_PRICE_GTE, String(priceGte))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_PRICE_GTE)
    }
    replaceSearchUrl(newUrlFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setBoundsSearchFilter = useCallback((bounds: string) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (bounds) {
      newUrlFilters.set(SEARCH_FILTERS_BOUNDS, bounds)
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_BOUNDS)
    }
    replaceSearchUrl(newUrlFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  const setLuxPlusFeaturesFilter = useCallback((luxPlusFeatures: Array<App.LuxPlusFeatureFilter> | null) => {
    const newUrlFilters = new URLSearchParams(queryParameters)
    if (luxPlusFeatures) {
      newUrlFilters.set(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE, luxPlusFeatures.join(','))
    } else {
      newUrlFilters.delete(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)
    }
    replaceSearchUrl(newUrlFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters])

  return {
    searchFilters,
    setSearchFilters,
    setBoundsSearchFilter,
    setCategoryCodesSearchFilter,
    setPriceLteSearchFilter,
    setPriceGteSearchFilter,
    setLuxPlusFeaturesFilter,
  }
}

function extractSearchFiltersFromUrl(urlFilters: URLSearchParams): Experiences.SearchFilters {
  const searchFilters: Experiences.SearchFilters = {}

  if (urlFilters.has(SEARCH_FILTERS_CATEGORY_CODES)) {
    searchFilters.categoryCodes = urlFilters.get(SEARCH_FILTERS_CATEGORY_CODES)!.split(',').map(i => Number(i))
  }

  if (urlFilters.has(SEARCH_FILTERS_PRICE_LTE)) {
    searchFilters.priceLte = Number(urlFilters.get(SEARCH_FILTERS_PRICE_LTE))
  }

  if (urlFilters.has(SEARCH_FILTERS_PRICE_GTE)) {
    searchFilters.priceGte = Number(urlFilters.get(SEARCH_FILTERS_PRICE_GTE))
  }

  if (urlFilters.has(SEARCH_FILTERS_BOUNDS)) {
    searchFilters.bounds = urlFilters.get(SEARCH_FILTERS_BOUNDS)!
  }

  if (urlFilters.has(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)) {
    searchFilters.luxPlusFeatures = urlFilters.get(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)?.split(',').map(i => i as App.LuxPlusFeatureFilter)
  }

  return searchFilters
}

function convertSearchFiltersToUrl(newFilters: Experiences.SearchFilters, currentSearchParams: URLSearchParams) {
  const searchParams = new URLSearchParams(currentSearchParams)

  if (newFilters.priceLte) {
    searchParams.set(SEARCH_FILTERS_PRICE_LTE, String(newFilters.priceLte))
  } else {
    searchParams.delete(SEARCH_FILTERS_PRICE_LTE)
  }

  if (newFilters.priceGte) {
    searchParams.set(SEARCH_FILTERS_PRICE_GTE, String(newFilters.priceGte))
  } else {
    searchParams.delete(SEARCH_FILTERS_PRICE_GTE)
  }

  if (newFilters.bounds) {
    searchParams.set(SEARCH_FILTERS_BOUNDS, newFilters.bounds)
  } else {
    searchParams.delete(SEARCH_FILTERS_BOUNDS)
  }

  if (newFilters.categoryCodes?.length) {
    searchParams.set(SEARCH_FILTERS_CATEGORY_CODES, newFilters.categoryCodes.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_CATEGORY_CODES)
  }

  if (newFilters.luxPlusFeatures) {
    searchParams.set(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE, newFilters.luxPlusFeatures.join(','))
  } else {
    searchParams.delete(SEARCH_FILTERS_LUX_PLUS_EXCLUSIVE)
  }

  return searchParams
}
